import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { MoreHorizontal, Plus, Search, ArrowUpDown } from 'lucide-react';
import { toast } from 'sonner';
import { useAdminTenants, useGrantCredits, useAdjustMonthlyCredits, useDisableTenant, useEnableTenant, type AdminTenant } from '@/hooks/api/useAdminApi';

type DialogType = 'grant-credits' | 'adjust-monthly' | 'disable' | 'enable' | null;

export default function TenantList() {
  const navigate = useNavigate();

  // Filters and search
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [subscriptionFilter, setSubscriptionFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);

  // Dialog state
  const [dialogType, setDialogType] = useState<DialogType>(null);
  const [selectedTenant, setSelectedTenant] = useState<AdminTenant | null>(null);
  const [creditAmount, setCreditAmount] = useState('');
  const [monthlyCredits, setMonthlyCredits] = useState('');

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1); // Reset to first page on search
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch tenants
  const { data, isLoading, error } = useAdminTenants({
    search: debouncedSearch || undefined,
    sort: sortBy,
    order: sortOrder,
    status: statusFilter !== 'all' ? (statusFilter as 'enabled' | 'disabled') : undefined,
    subscription: subscriptionFilter !== 'all' ? subscriptionFilter : undefined,
    page,
    limit: 20,
  });

  // Mutations
  const grantCredits = useGrantCredits();
  const adjustMonthly = useAdjustMonthlyCredits();
  const disableTenant = useDisableTenant();
  const enableTenant = useEnableTenant();

  // Handlers
  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const openDialog = (type: DialogType, tenant: AdminTenant) => {
    setDialogType(type);
    setSelectedTenant(tenant);
    if (type === 'adjust-monthly') {
      setMonthlyCredits(tenant.monthlyCredits.toString());
    }
  };

  const closeDialog = () => {
    setDialogType(null);
    setSelectedTenant(null);
    setCreditAmount('');
    setMonthlyCredits('');
  };

  const handleGrantCredits = async () => {
    if (!selectedTenant) return;
    const amount = parseInt(creditAmount);
    if (isNaN(amount) || amount < 1 || amount > 10000) {
      toast.error('Amount must be between 1 and 10,000');
      return;
    }

    try {
      await grantCredits.mutateAsync({ tenantId: selectedTenant.id, amount });
      toast.success(`Granted ${amount} credits to ${selectedTenant.companyName}`);
      closeDialog();
    } catch (err: any) {
      toast.error(err.message || 'Failed to grant credits');
    }
  };

  const handleAdjustMonthly = async () => {
    if (!selectedTenant) return;
    const credits = parseInt(monthlyCredits);
    if (isNaN(credits) || credits < 0 || credits > 10000) {
      toast.error('Monthly credits must be between 0 and 10,000');
      return;
    }

    try {
      await adjustMonthly.mutateAsync({ tenantId: selectedTenant.id, monthlyCredits: credits });
      toast.success(`Updated monthly credits for ${selectedTenant.companyName} to ${credits}`);
      closeDialog();
    } catch (err: any) {
      toast.error(err.message || 'Failed to adjust monthly credits');
    }
  };

  const handleDisable = async () => {
    if (!selectedTenant) return;

    try {
      await disableTenant.mutateAsync(selectedTenant.id);
      toast.success(`Disabled ${selectedTenant.companyName}`);
      closeDialog();
    } catch (err: any) {
      toast.error(err.message || 'Failed to disable tenant');
    }
  };

  const handleEnable = async () => {
    if (!selectedTenant) return;

    try {
      await enableTenant.mutateAsync(selectedTenant.id);
      toast.success(`Enabled ${selectedTenant.companyName}`);
      closeDialog();
    } catch (err: any) {
      toast.error(err.message || 'Failed to enable tenant');
    }
  };

  const getSubscriptionBadgeVariant = (status: string) => {
    switch (status) {
      case 'active':
        return 'default';
      case 'trial':
        return 'secondary';
      case 'past_due':
        return 'destructive';
      case 'canceled':
        return 'outline';
      default:
        return 'outline';
    }
  };

  if (error) {
    return (
      <div className="p-6">
        <h2 className="text-2xl font-bold text-destructive">Error Loading Tenants</h2>
        <p className="text-muted-foreground mt-2">{(error as Error).message}</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Tenants</h2>
          <p className="text-muted-foreground mt-1">
            Manage contractor accounts and credits
          </p>
        </div>
        <Button onClick={() => navigate('/admin/tenants/new')}>
          <Plus className="mr-2 h-4 w-4" />
          New Tenant
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by company name or slug..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={subscriptionFilter} onValueChange={setSubscriptionFilter}>
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder="Subscription" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Subscriptions</SelectItem>
            <SelectItem value="trial">Trial</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="past_due">Past Due</SelectItem>
            <SelectItem value="canceled">Canceled</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-[140px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="enabled">Enabled</SelectItem>
            <SelectItem value="disabled">Disabled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <button
                  onClick={() => handleSort('companyName')}
                  className="flex items-center gap-1 font-medium hover:text-foreground"
                >
                  Company
                  <ArrowUpDown className="h-3 w-3" />
                </button>
              </TableHead>
              <TableHead>Slug</TableHead>
              <TableHead>Owner</TableHead>
              <TableHead>Subscription</TableHead>
              <TableHead className="text-right">Credits</TableHead>
              <TableHead className="text-right">Leads</TableHead>
              <TableHead>
                <button
                  onClick={() => handleSort('createdAt')}
                  className="flex items-center gap-1 font-medium hover:text-foreground"
                >
                  Created
                  <ArrowUpDown className="h-3 w-3" />
                </button>
              </TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-[50px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-40" /></TableCell>
                  <TableCell><Skeleton className="h-5 w-16" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-12 ml-auto" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-8 ml-auto" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                  <TableCell><Skeleton className="h-5 w-16" /></TableCell>
                  <TableCell><Skeleton className="h-8 w-8" /></TableCell>
                </TableRow>
              ))
            ) : data?.data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                  No tenants found
                </TableCell>
              </TableRow>
            ) : (
              data?.data.map((tenant) => (
                <TableRow key={tenant.id}>
                  <TableCell>
                    <button
                      onClick={() => navigate(`/admin/tenants/${tenant.id}`)}
                      className="font-medium hover:underline text-left"
                    >
                      {tenant.companyName}
                    </button>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{tenant.slug}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {tenant.ownerEmail || 'No owner'}
                  </TableCell>
                  <TableCell>
                    <Badge variant={getSubscriptionBadgeVariant(tenant.subscriptionStatus)}>
                      {tenant.subscriptionStatus}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {tenant.totalCredits}
                  </TableCell>
                  <TableCell className="text-right">{tenant.leadCount}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {new Date(tenant.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Badge variant={tenant.isDisabled ? 'destructive' : 'default'}>
                      {tenant.isDisabled ? 'Disabled' : 'Enabled'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => navigate(`/admin/tenants/${tenant.id}`)}
                        >
                          View Details
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => openDialog('grant-credits', tenant)}
                        >
                          Grant Credits
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => openDialog('adjust-monthly', tenant)}
                        >
                          Adjust Monthly
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            openDialog(tenant.isDisabled ? 'enable' : 'disable', tenant)
                          }
                        >
                          {tenant.isDisabled ? 'Enable' : 'Disable'}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {data && data.meta.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Page {data.meta.page} of {data.meta.totalPages} ({data.meta.total} total)
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page + 1)}
              disabled={page >= data.meta.totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Grant Credits Dialog */}
      <Dialog open={dialogType === 'grant-credits'} onOpenChange={closeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Grant Addon Credits</DialogTitle>
            <DialogDescription>
              Grant one-time addon credits to {selectedTenant?.companyName}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="credit-amount">Credit Amount</Label>
              <Input
                id="credit-amount"
                type="number"
                min="1"
                max="10000"
                placeholder="Enter amount (1-10000)"
                value={creditAmount}
                onChange={(e) => setCreditAmount(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>
              Cancel
            </Button>
            <Button
              onClick={handleGrantCredits}
              disabled={grantCredits.isPending}
            >
              {grantCredits.isPending ? 'Granting...' : 'Grant Credits'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Adjust Monthly Dialog */}
      <Dialog open={dialogType === 'adjust-monthly'} onOpenChange={closeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adjust Monthly Credits</DialogTitle>
            <DialogDescription>
              Set the monthly credit allocation for {selectedTenant?.companyName}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="monthly-credits">Monthly Credits</Label>
              <Input
                id="monthly-credits"
                type="number"
                min="0"
                max="10000"
                placeholder="Enter amount (0-10000)"
                value={monthlyCredits}
                onChange={(e) => setMonthlyCredits(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>
              Cancel
            </Button>
            <Button
              onClick={handleAdjustMonthly}
              disabled={adjustMonthly.isPending}
            >
              {adjustMonthly.isPending ? 'Updating...' : 'Update Credits'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Disable Tenant Dialog */}
      <Dialog open={dialogType === 'disable'} onOpenChange={closeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Disable Tenant</DialogTitle>
            <DialogDescription>
              Are you sure you want to disable {selectedTenant?.companyName}? This will prevent
              widget access and all lead generation.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDisable}
              disabled={disableTenant.isPending}
            >
              {disableTenant.isPending ? 'Disabling...' : 'Disable Tenant'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Enable Tenant Dialog */}
      <Dialog open={dialogType === 'enable'} onOpenChange={closeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Enable Tenant</DialogTitle>
            <DialogDescription>
              Re-enable access for {selectedTenant?.companyName}?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>
              Cancel
            </Button>
            <Button
              onClick={handleEnable}
              disabled={enableTenant.isPending}
            >
              {enableTenant.isPending ? 'Enabling...' : 'Enable Tenant'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
